import {
	createStyles,
	getStylesRef,
	Group,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { Icon, Text } from '@repo/foundations';
import type { PropsWithChildren, ReactNode } from 'react';

const useStyles = createStyles(
	(theme, { open, forceOpen }: { open: boolean; forceOpen?: boolean }) => ({
		displayOnHover: {
			ref: getStylesRef('displayOnHover'),
			opacity: 0,
		},
		chevron: {
			transform: open ? 'rotate(90deg)' : 'none',
			transition: 'transform 100ms ease',
			color: theme.other.getColor('icon/secondary/default'),
			display: forceOpen ? 'none' : 'block',
		},
		wrapper: {
			paddingLeft: theme.other.space[1],
			borderRadius: theme.other.borderRadius.sm,
			':hover': {
				backgroundColor: forceOpen
					? 'transparent'
					: theme.other.getColor('fill/transparent/hover'),
			},
			':active': {
				backgroundColor: forceOpen
					? 'transparent'
					: theme.other.getColor('fill/transparent/active'),
			},
			[`&:hover .${getStylesRef('displayOnHover')}`]: {
				opacity: forceOpen ? 0 : 1,
			},
		},
		button: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'flex-start',
			alignItems: 'center',
			height: theme.other.space[7],
			flex: 1,
			cursor: forceOpen ? 'default' : 'pointer',
		},
	})
);

export interface CategoryToggleProps {
	label: ReactNode;
	labelRightSection?: ReactNode;
	toggleKey: string;
	forceOpen?: boolean;
}

export function SideBarCategoryToggle({
	label,
	toggleKey,
	children,
	labelRightSection,
	forceOpen,
}: PropsWithChildren<CategoryToggleProps>) {
	const [collapsableOpened, setCollapsableOpened] = useLocalStorage({
		key: toggleKey,
		defaultValue: true,
	});

	const isOpen = forceOpen === true ? true : collapsableOpened;
	const { classes, cx } = useStyles({ open: isOpen, forceOpen });

	return (
		<Stack spacing={0}>
			<Group spacing="xs" position="apart" noWrap className={classes.wrapper}>
				<UnstyledButton
					className={classes.button}
					onClick={() => !forceOpen && setCollapsableOpened((prev) => !prev)}
				>
					<Text size="xs" color="text/secondary/default" weight="bold">
						{label}
					</Text>
					<Icon
						name="chevronRight"
						className={cx(classes.displayOnHover, classes.chevron)}
					/>
				</UnstyledButton>
				{labelRightSection}
			</Group>
			{isOpen && children}
		</Stack>
	);
}
